import { useState, type SubmitEvent } from "react";
import { useTranslation } from "react-i18next";
import type { ImportMode } from "../../../api/content/corporaApi";
import { uploadDocuments } from "../../../api/content/hostingApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useSubmission } from "../../../shared/state/useSubmission";
import Field from "../../../ui/forms/Field";
import FileDropzone from "../../../ui/forms/FileDropzone";
import type { PickedFiles } from "../../../ui/forms/pickedFiles";
import { FORM_HELP_TEXT_CLASS, FORM_SELECT_CLASS } from "../../../ui/styles/formStyles";
import HelpArticleLink from "../../help/HelpArticleLink";
import { isJsonLinesFile, isTemplateText, openTargetCorpus, runCorpusImport, segmentationTemplateText, type CorpusImportOutcome } from "./corpusImport";
import CorpusTargetFields from "./CorpusTargetFields";
import ImportResultSummary from "./ImportResultSummary";
import ImportSubmit, { type ImportBlocker } from "./ImportSubmit";
import ImportWorkflowField from "./ImportWorkflowField";
import { useCorpusTarget, useImportWorkflow, useUploadWorkflow, workflowBlocker, type ImportFormProps } from "./importSetup";
import SourceImportSummary, { type SourceImportOutcome } from "./SourceImportSummary";
import { uploadArchive } from "./uploadArchive";

const IMPORT_MODES: readonly ImportMode[] = ["upsert", "replace_all"];

type Outcome = { readonly kind: "jsonLines"; readonly result: CorpusImportOutcome } | SourceImportOutcome;

/**
 * Lokal (web-app.md WA-9, F16): a file or a folder from this PC. Documents are uploaded and
 * run through the import Workflow in a Thread. The first upload into a corpus chooses it and
 * creates the corpus's upload source; later uploads run that one, which the form shows. One JSON
 * Lines file is imported directly, with the segmentation given here.
 */
export default function LocalImportForm({ projectId, corpusId, onStarted }: ImportFormProps) {
  const { t } = useTranslation();
  const corpus = useCorpusTarget(projectId, corpusId, "fromInput");
  const update = useImportWorkflow(projectId);
  const pinned = useUploadWorkflow(corpus.existingCorpusId);
  const [picked, setPicked] = useState<PickedFiles | null>(null);
  const [mode, setMode] = useState<ImportMode>("upsert");
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const { state, submit } = useSubmission();
  const jsonLines = picked !== null && isJsonLinesFile(picked.files);

  const pick = (next: PickedFiles | null) => {
    setPicked(next);
    setOutcome(null);
    // Documents take the converter's segments; JSON Lines are cut by rules, and an untouched template follows that.
    const nextJsonLines = next !== null && isJsonLinesFile(next.files);
    if (next !== null && (!nextJsonLines || isTemplateText(corpus.draft.segmentationText))) {
      corpus.setDraft({ ...corpus.draft, segmentationText: segmentationTemplateText(nextJsonLines ? "flat" : "fromInput") });
    }
  };
  const handleSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    const target = corpus.target();
    // A corpus with uploads runs the Workflow its first upload pinned; only a first upload chooses.
    const workflow = (pinned.state.status === "loaded" ? pinned.state.value : null) ?? update.workflow?.reference ?? null;
    if (picked === null || target === null) return;
    setOutcome(null);
    submit(async () => {
      try {
        if (jsonLines && picked.files[0] !== undefined) {
          const result = await runCorpusImport(target, picked.files[0].file, mode);
          setOutcome({ kind: "jsonLines", result });
          corpus.imported(result.corpusId, "flat");
        } else if (workflow !== null) {
          const opened = await openTargetCorpus(target);
          const archive = uploadArchive(picked.files.map((file) => ({ path: file.path, content: file.file })));
          const upload = await uploadDocuments(opened.corpusId, archive, workflow);
          setOutcome({ kind: "upload", corpusName: opened.name, upload });
          corpus.imported(opened.corpusId, "fromInput");
        }
        setPicked(null);
      } finally {
        // A corpus created before a failed import exists all the same.
        corpus.reloadCorpora();
      }
    });
  };
  const blocker: ImportBlocker | null = picked === null ? "chooseFiles" : (jsonLines ? null : workflowBlocker(update, pinned.state)) ?? (corpus.nameMissing ? "nameCorpus" : null);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <fieldset disabled={state.status === "submitting"} className="space-y-4">
        <Field label={t("sources.import.files")} actions={<HelpArticleLink article="localImport" />}>
          {(control) => <FileDropzone control={control} picked={picked} onPick={pick} hint={t("sources.import.filesHint")} />}
        </Field>
        {picked === null ? null : <p className={FORM_HELP_TEXT_CLASS}>{t(jsonLines ? "sources.import.asJsonLines" : "sources.import.asDocuments")}</p>}
        <CorpusTargetFields {...corpus.fields} jsonLines={jsonLines} />
        {jsonLines ? (
          <Field label={t("sources.import.mode")}>
            {(control) => (
              <select {...control} className={FORM_SELECT_CLASS} value={mode} onChange={(event) => { setMode(IMPORT_MODES.find((candidate) => candidate === event.target.value) ?? "upsert"); }}>
                {IMPORT_MODES.map((importMode) => <option key={importMode} value={importMode}>{t(`sources.import.modes.${importMode}`)}</option>)}
              </select>
            )}
          </Field>
        ) : <ImportWorkflowField projectId={projectId} choice={update} pinned={pinned.state.status === "loaded" ? pinned.state.value : null} />}
      </fieldset>
      {state.status === "failed" ? <ErrorNotice error={state.error} /> : null}
      {outcome?.kind === "jsonLines" ? <ImportResultSummary outcome={outcome.result} /> : outcome === null ? null : <SourceImportSummary projectId={projectId} outcome={outcome} onStarted={onStarted} />}
      <ImportSubmit label={state.status === "submitting" ? t("sources.import.importing") : t(jsonLines ? "sources.import.submit" : "sources.import.submitRun")} blocker={blocker} submitting={state.status === "submitting"} />
    </form>
  );
}
